<template>
    <div class="timesheet">
        <div class="header">
            <el-row>
                <el-col>
                    <div class="page-title bold fnt-lg dark text-left">
                        Табель сотрудников
                    </div>
                </el-col>
            </el-row>
        </div>
        <div class="grid">
            <el-row class="mb10">
                <el-col :span="6" class="pt10">
                    <el-checkbox-group v-model="selectedPositionIds">
                        <el-checkbox class="position-item" :label="referencePosition.measurer" v-if="isMeasurersVisible">
                            Замерщики
                        </el-checkbox>
                        <el-checkbox class="position-item" :label="referencePosition.installer" v-if="isInstallersVisible">
                            Установщики
                        </el-checkbox>
                        <el-checkbox class="position-item" :label="referencePosition.manager" v-if="isManagersVisible">
                            Менеджеры
                        </el-checkbox>
                    </el-checkbox-group>
                </el-col>
                <el-col :span="3" class="ml-15">
                    <el-date-picker
                        v-model="selectedMonth"
                        type="month"
                        format="MMMM yyyy"
                        value-format="yyyy-MM-dd"
                        placeholder="Pick a month"
                        :clearable="false"
                        :editable="false"
                        @change="handleSelectedMonth"
                    />
                </el-col>
                <el-col :span="4" class="text-center bold timesheet-top-filter-fired">
                    <span>Показывать уволенных</span>
                    <el-checkbox v-model="showFired" @change="getTimesheet" />
                </el-col>
            </el-row>
            <el-row v-loading="loading">
                <el-col>
                    <div v-for="element in timesheetByCategories" :key="element.id" class="categoryElement">
                        <div class="categoryTitle name" v-if="element.employers.length" @click="handleElementClick(element)">
                            <div class="categoryRaw">
                                <i class="el-icon-caret-bottom" v-if="element.isOpened"></i>
                                <i class="el-icon-caret-right" v-else></i>
                                {{ element.name }}
                            </div>
                        </div>
                        <table v-if="element.isOpened">
                            <thead>
                                <tr v-if="element.employers.length">
                                    <th class="title-cell">
                                        Сотрудник
                                    </th>
                                    <th class="title-cell">
                                        Должность
                                    </th>
                                    <th v-for="date in daysRange" :key="date">
                                        {{ date }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="employer in element.employers" :key="employer.id" :class="isFiredEmployerClass(employer.date_fired)">
                                    <td class="title-cell">
                                        {{ employer.full_name }}
                                    </td>
                                    <td class="title-cell">
                                        <span class="mr8">
                                            {{ employer.position.name }}
                                        </span>
                                        <el-button
                                            size="mini"
                                            type="warning"
                                            v-if="employer.changed"
                                            @click="updateTimesheet(employer.id, employer.position_id)"
                                        >
                                            сохранить
                                        </el-button>
                                    </td>
                                    <template v-if="employer.timesheets.length">
                                        <td
                                            v-for="timesheet in employer.timesheets"
                                            :key="timesheet.id"
                                            @click="showDropdown($event, employer.editable, employer.id, timesheet.id)"
                                            :class="[
                                                'time-cell',
                                                {'changed-cell': timesheet.changed},
                                                {'pointer': employer.editable},
                                                `time-cell-${timesheet.status_id}`
                                            ]"
                                        >
                                            {{ timesheet.status.short }}
                                        </td>
                                    </template>
                                    <template v-else>
                                        <td
                                            v-for="date in daysRange"
                                            :key="date"
                                            class="time-cell"
                                        >
                                        </td>
                                    </template>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div>
                        <ul class="dropdown-menu" v-if="isDropdownVisible" :style="dropdownOptions">
                            <li
                                v-for="status in statusList"
                                :key="status.id"
                                tabindex="-1"
                                :class="['dropdown-menu__item', {'menu-disabled': status.disabled}, `time-cell-${status.id}`]"
                                @click="changeDayStatus(status)"
                            >
                                {{ status.short }}
                            </li>
                        </ul>
                    </div>
                </el-col>
            </el-row>
        </div>
    </div>
</template>

<script>
import '@/assets/styles/timesheet.scss';
import localReferences from '@/helpers/localReferences';
import roleHelper from '@/helpers/roleHelper';
import moment from 'moment';
import { formatFinance } from '@/common/utilities';

export default {
    name: 'Timesheet',
    data() {
        return {
            showFired: 0,
            loading: false,
            isTimesheetReady: false,
            daysRange: [],
            isDropdownVisible: false,
            dropdownOptions: {
                top: 0,
                left: 0
            },
            selectedEmployerId: null,
            selectedTimesheetId: null,
            selectedPositionIds: [],
            referencePosition: localReferences.employerPositions,
            selectedMonth: moment().startOf('month').format('YYYY-MM-DD'),
            selectedMonthLastDay: moment().endOf('month').format('YYYY-MM-DD'),
            timesheetByCategories: [{ employers: [], id: 0, isOpened: false, name: '' }]
        };
    },
    computed: {
        employersWithTimesheet() {
            return this.$store.state.timesheet.data.filter(employer => this.selectedPositionIds.includes(employer.position_id));
        },
        statusList() {
            return this.$store.state.references.timesheetStatuses.map(status => { return { ...status, comment: '', disabled: false }; });
        },
        userTimesheetRoles() {
            return this.$store.getters.timesheetRoles;
        },
        isManagersAll() {
            return this.userTimesheetRoles.includes(roleHelper.timesheet_managers_all.key);
        },
        isManagersSelf() {
            return this.userTimesheetRoles.includes(roleHelper.timesheet_managers_self.key);
        },
        isInstallersAll() {
            return this.userTimesheetRoles.includes(roleHelper.timesheet_installers_all.key);
        },
        isInstallersSelf() {
            return this.userTimesheetRoles.includes(roleHelper.timesheet_installers_self.key);
        },
        isMeasurersAll() {
            return this.userTimesheetRoles.includes(roleHelper.timesheet_measurers_all.key);
        },
        isMeasurersSelf() {
            return this.userTimesheetRoles.includes(roleHelper.timesheet_measurers_self.key);
        },
        isManagersVisible() {
            return this.isManagersAll || this.isManagersSelf || this.isMeasurersAll || this.isMeasurersSelf;
        },
        isMeasurersVisible() {
            return this.isManagersAll || this.isManagersSelf || this.isMeasurersAll || this.isMeasurersSelf;
        },
        isInstallersVisible() {
            return this.isManagersAll || this.isManagersSelf || this.isInstallersAll || this.isInstallersSelf;
        },
        selectedEmployer() {
            if (!this.selectedEmployerId) {
                return { position_id: null, timesheets: [] };
            }
            return this.employersWithTimesheet.find(employer => employer.id === this.selectedEmployerId);
        },
        selectedDay() {
            if (!this.selectedTimesheetId) {
                return null;
            }
            const timesheet = this.selectedEmployer.timesheets.find(timesheet => timesheet.id === this.selectedTimesheetId);
            return timesheet ? timesheet.day : null;
        },
        selectedEmployerOffDaysCount() {
            return this.selectedEmployer.timesheets.filter(timesheet => timesheet.status_id === localReferences.timesheet.statuses.dayOff).length;
        },
        selectedEmployerOffDayAfterTodayPeriod() {
            return this.selectedDay ? moment(this.selectedDay).diff(moment(), 'days') : 0;
        },
        selectedEmployersPositionCount() {
            return this.employersWithTimesheet.filter(employer => employer.position_id === this.selectedEmployer.position_id)
                .filter(employer => employer.timesheets.filter(timesheet => timesheet.day === this.selectedDay &&
                        timesheet.status_id === localReferences.timesheet.statuses.working).length > 0).length;
        }
    },
    methods: {
        handleElementClick(element) {
            element.isOpened = !element.isOpened;
        },
        handleSelectedMonth(value) {
            this.selectedMonthLastDay = moment(value).endOf('month').format('YYYY-MM-DD');
            this.getTimesheet();
        },
        changeDayStatus(status) {
            if (status.disabled) {
                return;
            }
            this.$store.commit('timesheet/changeStatus',
                {
                    status: { ...status },
                    employerId: this.selectedEmployerId,
                    timesheetId: this.selectedTimesheetId
                });
            this.isDropdownVisible = !this.isDropdownVisible;
        },
        getOffsetRect(elem) {
            var box = elem.getBoundingClientRect();
            var body = document.body;
            var docElem = document.documentElement;
            var scrollTop = window.pageYOffset || docElem.scrollTop || body.scrollTop;
            var scrollLeft = window.pageXOffset || docElem.scrollLeft || body.scrollLeft;
            var clientTop = docElem.clientTop || body.clientTop || 0;
            var clientLeft = docElem.clientLeft || body.clientLeft || 0;
            var top = box.top + scrollTop - clientTop;
            var left = box.left + scrollLeft - clientLeft;

            return { top: Math.round(top), left: Math.round(left) };
        },
        showDropdown(event, editable, employerId, timesheetId) {
            const pos = this.getOffsetRect(event.target);
            if (!editable) {
                return;
            }
            this.isDropdownVisible = !this.isDropdownVisible;
            if (this.isDropdownVisible) {
                this.dropdownOptions.top = `${pos.top - 237}px`;
                this.dropdownOptions.left = `${pos.left - 289}px`;
                this.selectedEmployerId = employerId;
                this.selectedTimesheetId = timesheetId;
                this.checkSelectedEmployerDayOffConstrains();
            }
        },
        checkSelectedEmployerDayOffConstrains() {
            if (
                (this.isMeasurersAll && this.selectedEmployer.position_id === localReferences.employerPositions.measurer) ||
                (this.isManagersAll && this.selectedEmployer.position_id === localReferences.employerPositions.manager) ||
                (this.isInstallersAll && this.selectedEmployer.position_id === localReferences.employerPositions.installer)
            ) {
                //для ролей с полным доступом ограничения не проверяем
                return;
            }
            const constrains = {
                offDaysCount: false,
                offDayAfterTodayPeriod: false,
                positionCount: false
            };
            const statusDayOff = this.statusList.find(status => status.id === localReferences.timesheet.statuses.dayOff);
            const statusSick = this.statusList.find(status => status.id === localReferences.timesheet.statuses.sick);
            const statusVacation = this.statusList.find(status => status.id === localReferences.timesheet.statuses.vacation);
            const statusWorking = this.statusList.find(status => status.id === localReferences.timesheet.statuses.working);
            statusDayOff.disabled = false;
            statusSick.disabled = false;
            statusVacation.disabled = false;
            statusWorking.disabled = false;
            const message = this.selectedEmployer.full_name + ' ';
            if (this.selectedEmployerOffDaysCount >= localReferences.timesheet.constraints.maxDayOffPerMonth) {
                constrains.offDaysCount = true;
                setTimeout(() => {
                    this.showNotify(`${message} нельзя поставить более ${localReferences.timesheet.constraints.maxDayOffPerMonth} выходных в месяц`);
                }, 1);
            }
            if (this.selectedEmployerOffDayAfterTodayPeriod < localReferences.timesheet.constraints.daysBeforeOthersCanTakeDayOff &&
                this.selectedEmployerOffDayAfterTodayPeriod >= 0
            ) {
                if (this.selectedEmployer.position_id !== localReferences.employerPositions.manager) {
                    this.showNotify(`${message} нельзя поставить выходной ранее чем за ${localReferences.timesheet.constraints.daysBeforeOthersCanTakeDayOff} дней от текущей даты`);
                    constrains.offDayAfterTodayPeriod = true;
                } else if (this.selectedEmployerOffDayAfterTodayPeriod < localReferences.timesheet.constraints.daysBeforeManagerCanTakeDayOff) {
                    this.showNotify(`${message} нельзя поставить нерабочий день ранее чем за ${localReferences.timesheet.constraints.daysBeforeManagerCanTakeDayOff} дня от текущей даты`);
                    constrains.offDayAfterTodayPeriod = true;
                }
            }
            if (this.selectedEmployerOffDayAfterTodayPeriod < 0 &&
                (
                    (this.selectedEmployer.position_id === localReferences.employerPositions.manager && this.isManagersSelf) ||
                    (this.selectedEmployer.position_id === localReferences.employerPositions.installer && this.isInstallersSelf) ||
                    (this.selectedEmployer.position_id === localReferences.employerPositions.measurer && this.isMeasurersSelf)
                )) {
                this.showNotify(`${message} нельзя изменять статус дня до текущей даты`);
                constrains.offDayAfterTodayPeriod = true;
                statusWorking.disabled = true;
            }
            if (this.selectedEmployer.position_id === localReferences.employerPositions.measurer && this.selectedEmployersPositionCount < localReferences.timesheet.constraints.minWorkingMeasurersPerDay) {
                setTimeout(() => {
                    this.showNotify(`${message} нельзя поставить выходной - в этот день работает менее ${localReferences.timesheet.constraints.minWorkingMeasurersPerDay} человек`);
                }, 2);
                constrains.positionCount = true;
            }
            statusDayOff.disabled = constrains.offDaysCount || constrains.offDayAfterTodayPeriod || constrains.positionCount;
            if (this.selectedEmployer.position_id === localReferences.employerPositions.measurer) {
                statusSick.disabled = constrains.offDayAfterTodayPeriod;
                statusVacation.disabled = constrains.offDayAfterTodayPeriod;
            } else {
                statusSick.disabled = false;
                statusVacation.disabled = false;
            }
        },
        showNotify(message) {
            this.$notify({
                title: 'Внимание',
                message,
                type: 'warning'
            });
        },
        async getTimesheet() {
            try {
                this.loading = true;
                await this.$store.dispatch('timesheet/get', {
                    startDay: this.selectedMonth,
                    endDay: this.selectedMonthLastDay,
                    showFired: +this.showFired
                });
                this.updateDaysRange();
            } catch (e) {
                console.log(e);
            } finally {
                this.isTimesheetReady = true;
                this.loading = false;
            }
            if (!this.isTimesheetReady) {
                return;
            }
            const timesheetFull = this.$store.state.timesheet.data.filter(employer => this.selectedPositionIds.includes(employer.position_id));
            const officeIds = [...new Set(timesheetFull.map(item => item.office_id))].sort();
            const timesheetResult = [];
            const otherEmployerPositions = [this.referencePosition.measurer, this.referencePosition.installer];
            const timesheetByOffices = timesheetFull.filter(item => (!otherEmployerPositions.includes(item.position_id)));
            var id = 0;
            officeIds.forEach(officeId => {
                const timesheetByOffice = timesheetByOffices.filter(item => item.office_id === officeId);
                const employersByOffice = {
                    name: this.getOfficeName(officeId),
                    id: id++,
                    isOpened: 0,
                    employers: timesheetByOffice
                };
                timesheetResult.push(employersByOffice);
            });

            const timesheetOtherEmployers = timesheetFull.filter(item => otherEmployerPositions.includes(item.position_id));
            otherEmployerPositions.forEach(positionId => {
                const timesheetByOtherEmployerPositions = timesheetOtherEmployers.filter(item => item.position_id === positionId);
                const otherEmployers = {
                    name: this.getPositionName(positionId),
                    id: id++,
                    isOpened: 0,
                    employers: timesheetByOtherEmployerPositions
                };
                timesheetResult.push(otherEmployers);
            });
            this.timesheetByCategories = timesheetResult;
            return true;
        },
        async updateTimesheet(employerId, positionId) {
            try {
                this.loading = true;
                await this.$store.dispatch('timesheet/update', { employerId, positionId });
            } catch (e) {
                console.log(e);
            } finally {
                this.loading = false;
            }
        },
        updateDaysRange() {
            const days = [];
            for (let i = 1; i <= this.selectedMonthLastDay.split('-')[2]; i++) {
                days.push(i <= 9 ? '0' + i : i);
            }
            this.daysRange = days;
        },
        updateSelectedPositions() {
            const positionsIds = [];
            if (this.isMeasurersVisible) {
                positionsIds.push(this.referencePosition.measurer);
            }
            if (this.isInstallersVisible) {
                positionsIds.push(this.referencePosition.installer);
            }
            if (this.isManagersVisible) {
                positionsIds.push(this.referencePosition.manager);
                positionsIds.push(this.referencePosition.measurer);
                positionsIds.push(this.referencePosition.installer);
            }
            this.selectedPositionIds = [...new Set(positionsIds)];
        },
        getOfficeName(officeId) {
            const office = this.$store.state.references.offices.find(office => office.id === officeId);
            return office ? office.name : '';
        },
        getPositionName(positionId) {
            if (positionId === this.referencePosition.measurer) {
                return 'Замерщики';
            }
            if (positionId === this.referencePosition.installer) {
                return 'Установщики';
            }
            return null;
        },
        isFiredEmployerClass(dateFired) {
            return dateFired ? 'fired_employer' : '';
        }
    },
    mounted() {
        this.getTimesheet();
        this.updateSelectedPositions();
    },
    beforeDestroy() {
        this.$store.commit('timesheet/clearStore');
    },
    watch: {
        isTimesheetReady() {
            this.getTimesheet();
        },
        selectedPositionIds() {
            this.getTimesheet();
        }
    }
};
</script>
